'use client';

/**
 * OrderChatBadge — sidebar badge with unread order-chat count.
 *
 * PR #1438 (v124.2 — follow-up #A2): mirrors `DirectMessageBadge`
 * (self-contained — NO hook dependency, polls + WS listener inline).
 *
 * Founder directive 2026-10-18 (PR #1433): buyer↔seller chat per order.
 * Follow-up #A2: "indicador visual en el sidebar cuando hay mensajes de
 * órdenes sin leer" (WhatsApp-style — the badge appears next to "Mis
 * Compras" + a red dot on the parent "MARKETPLACE" group when collapsed).
 *
 * Mirrors `DirectMessageBadge` + `NotificationBadge`:
 *   - Polls `GET /api/market/orders/chat/unread-count` every 30s
 *     (lightweight — only fetches the count, not the messages). The
 *     endpoint + the `api.market.orders.chat.getUnreadCount()` typed
 *     wrapper were added in follow-up #A1 (see market-orders.ts +
 *     order-chat.controller.ts:getUnreadCount).
 *   - Listens to the `chat:message:new` WebSocket event (emitted by
 *     `order-chat.service.ts:sendMessage` to the `user:<recipientId>`
 *     room — the recipient's socket). On event, increments the count
 *     immediately (no need to wait for the next 30s poll).
 *   - Refetches the count when the user navigates to `/market/ordenes*`
 *     because the order-list page triggers the backend's
 *     `markThreadRead` when the user opens an order detail + the chat
 *     section loads (so the count drops when the user reviews the chats).
 *
 * Variants (same as NotificationBadge + DirectMessageBadge):
 *   variant='count' (default) — small red badge with the unread count,
 *                               shown next to the "Mis Compras" item.
 *   variant='dot'              — small solid red dot (no number), shown
 *                               next to the "MARKETPLACE" group label
 *                               header so the user sees an indicator
 *                               WITHOUT expanding the group.
 *
 * §6 Anti-Invención: the backend endpoint already exists — this component
 *   only READS the count. No business logic on the frontend.
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` calls.
 * Anti-P-043: AbortController for in-flight fetch cancellation on unmount.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { api, ApiClientError } from '@/lib/api-client';
import { useSocket } from '@/hooks/use-socket';

export interface OrderChatBadgeProps {
  /**
   * 'count' (default) — renders a small red badge with the unread count.
   * 'dot' — renders a small solid red dot (no number). Used on the
   * "MARKETPLACE" PARENT group label so the user sees a visual indicator
   * without expanding the group (WhatsApp-style).
   */
  variant?: 'count' | 'dot';
}

export function OrderChatBadge({ variant = 'count' }: OrderChatBadgeProps) {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const pathname = usePathname();
  // Destructure `on` + `isConnected` from useSocket so the WS listener
  // useEffect can depend on those stable references directly (avoids the
  // `react-hooks/exhaustive-deps` warning that fires when the whole
  // `socket` object — recreated every render — is omitted from the dep
  // array). `on` is a useCallback inside useSocket with `[isConnected]`
  // as its dep, so it only changes when the socket (re)connects.
  const { on: socketOn, isConnected: socketIsConnected } = useSocket();
  // AbortController ref — cancels the in-flight fetch on unmount / re-fetch.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch the unread count from the backend. Lightweight — only the count.
  // Silent-fail: the badge is non-critical; on 401 (logged out) we reset
  // to 0 (matches the NotificationBadge + DirectMessageBadge patterns).
  const fetchCount = useCallback(async (): Promise<void> => {
    // Cancel any previous in-flight request before starting a new one.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const res = await api.market.orders.chat.getUnreadCount(controller.signal);
      setUnreadCount(res.count);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        // User logged out — don't show error, just set count to 0.
        setUnreadCount(0);
      }
      // Other errors (network, 500, AbortError) — leave the previous
      // count as-is. The badge is non-critical; the next 30s poll will
      // retry.
    }
  }, []);

  // Initial fetch + polling (every 30s — matches NotificationBadge +
  // DirectMessageBadge cadence).
  useEffect(() => {
    void fetchCount();
    const interval = setInterval(() => {
      void fetchCount();
    }, 30_000);
    return () => {
      abortControllerRef.current?.abort();
      clearInterval(interval);
    };
  }, [fetchCount]);

  // Refetch when navigating to /market/ordenes (the buyer's order-list
  // page). The backend's `markThreadRead` is called when the user opens
  // an order detail + the chat section loads — so the count drops when
  // the user reviews the chats. Without this refetch, the badge would
  // show a stale (higher) count until the next 30s poll.
  useEffect(() => {
    if (pathname.startsWith('/market/ordenes')) {
      void fetchCount();
    }
  }, [pathname, fetchCount]);

  // WebSocket listener for `chat:message:new` events. The backend emits
  // this to `user:<recipientId>` (see `order-chat.service.ts:sendMessage`
  // line ~340). The `useSocket` hook auto-joins the user's room via the
  // backend `socket-auth.middleware.ts`, so any new message addressed to
  // the current user fires this event. We increment the count immediately
  // (no need to wait for the next 30s poll).
  //
  // Deps: `[socketOn, socketIsConnected]` — `socketOn` is a useCallback
  // that changes when `socketIsConnected` flips (see use-socket.ts), so
  // this effect re-subscribes only when the socket (re)connects, NOT on
  // every render.
  useEffect(() => {
    if (!socketIsConnected) return;
    const unsubscribe = socketOn('chat:message:new', (...args: unknown[]) => {
      // The backend emits the message payload (OrderChatMessageNewEvent
      // shape). We don't need to inspect it — any new message addressed
      // to the current user increments the unread count (the backend's
      // `io.to(`user:${recipientUserId}`).emit(...)` scopes the event to
      // the recipient; the sender's socket does NOT receive it).
      void args;
      setUnreadCount((prev) => prev + 1);
    });
    return unsubscribe;
  }, [socketOn, socketIsConnected]);

  // App Badging API — set/clear the app icon badge (mirror NotificationBadge).
  // navigator.setAppBadge(n) shows a number on the app icon (Android home
  // screen, Windows taskbar). navigator.clearAppBadge() removes it.
  // Graceful no-op on unsupported browsers (Firefox/Safari).
  // Idempotent: safe to call from multiple mounted instances (count + dot
  // + the OrderChatBadge instances from other sidebar mounts).
  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount).catch(() => {
          // Badge API can fail if the document is not focused or the app
          // is not installed. Silently ignore — the in-app badge still works.
        });
      } else {
        navigator.clearAppBadge().catch(() => {
          // Same — silently ignore.
        });
      }
    }
  }, [unreadCount]);

  // Don't render the badge if unreadCount is 0 (clean UI when no new chats)
  if (unreadCount === 0) return null;

  if (variant === 'dot') {
    // Small solid red dot — used on the "MARKETPLACE" PARENT group label
    // so the user sees an indicator WITHOUT expanding the group. Matches
    // the NotificationBadge + DirectMessageBadge dot variants for visual
    // consistency.
    return (
      <span
        aria-label={`${unreadCount} mensajes de órdenes sin leer`}
        className="inline-block size-2 shrink-0 rounded-full bg-destructive"
      />
    );
  }

  return (
    <Badge
      variant="destructive"
      className="ml-auto h-5 px-1.5 text-xs min-w-5 flex items-center justify-center"
      aria-label={`${unreadCount} mensajes de órdenes sin leer`}
    >
      {unreadCount > 99 ? '99+' : unreadCount}
    </Badge>
  );
}
